import { CLIENT, type Session } from './client';
import type { ConversationEvent, ExploreEvent, GradeEvent, IngestEvent, MapEvent } from './types';

export interface EventHandlers {
  ingest?: (e: IngestEvent) => void;
  grade?: (e: GradeEvent) => void;
  /** A round of Explore suggestions finished (status idle) or failed; refetch get-explore. */
  explore?: (e: ExploreEvent) => void;
  /** A map's build started (building), finished (ready) or failed; refetch get-map for that scope and book. */
  map?: (e: MapEvent) => void;
  /** An Explore conversation changed on some device (an answer started or finished, or it was deleted); refetch it and the list. */
  conversation?: (e: ConversationEvent) => void;
  /** Called after each (re)connection, so callers can refetch what they may have missed. */
  open?: () => void;
}

/**
 * Subscribes to /v1/events, on the session's cookies (same origin). It uses
 * fetch rather than EventSource so a 401 can renew the session and connect
 * again at once, and reconnects with backoff otherwise. Returns a function
 * that closes the stream.
 */
export function subscribeEvents(baseUrl: string, session: Session, h: EventHandlers): () => void {
  let closed = false;
  let controller: AbortController | null = null;
  let backoff = 1000;

  const run = async () => {
    while (!closed) {
      controller = new AbortController();
      try {
        const seen = session.csrf();
        if (!seen) throw new Error('signed out');
        const res = await fetch(`${baseUrl}/v1/events`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/event-stream', ...CLIENT },
          signal: controller.signal,
        });
        // The access cookie expired: renewed, the stream reconnects without waiting.
        if (res.status === 401 && (await session.refresh(seen))) continue;
        if (!res.ok || !res.body) throw new Error(`events: ${res.status}`);
        backoff = 1000;
        h.open?.();
        await readStream(res.body, (event, data) => {
          try {
            const parsed: unknown = JSON.parse(data);
            if (event === 'ingest') h.ingest?.(parsed as IngestEvent);
            else if (event === 'grade') h.grade?.(parsed as GradeEvent);
            else if (event === 'explore') h.explore?.(parsed as ExploreEvent);
            else if (event === 'map') h.map?.(parsed as MapEvent);
            else if (event === 'conversation') h.conversation?.(parsed as ConversationEvent);
          } catch {
            // ignore malformed frames
          }
        });
      } catch {
        if (closed) return;
      }
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 30_000);
    }
  };
  void run();
  return () => {
    closed = true;
    controller?.abort();
  };
}

/** Parses a server-sent event stream, calling onEvent per complete event. */
export async function readStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.search(/\r?\n\r?\n/)) >= 0) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep).replace(/^\r?\n\r?\n/, '');
      let event = 'message';
      const data: string[] = [];
      for (const line of frame.split(/\r?\n/)) {
        if (line.startsWith(':')) continue;
        const i = line.indexOf(':');
        const field = i < 0 ? line : line.slice(0, i);
        const val = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
        if (field === 'event') event = val;
        else if (field === 'data') data.push(val);
      }
      if (data.length) onEvent(event, data.join('\n'));
    }
  }
}
